import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Check, MessageSquare, Trophy, Target, Lightbulb, Sprout, Lock, Heart } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { createCheckoutSession, getCustomerPurchases } from '../services/paymentService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityPage = () => {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [communityStats, setCommunityStats] = useState(null);
  const [previewPosts, setPreviewPosts] = useState([]);

  useEffect(() => {
    // Load community stats and preview
    const loadPreview = async () => {
      try {
        const [statsRes, feedRes] = await Promise.all([
          fetch(`${API_URL}/api/community/stats`),
          fetch(`${API_URL}/api/community/feed?limit=3`)
        ]);
        const statsData = await statsRes.json();
        const feedData = await feedRes.json();
        setCommunityStats(statsData);
        setPreviewPosts(feedData.posts || []);
      } catch (err) {}
    };
    loadPreview();
  }, []);

  const features = [
    { icon: Lightbulb, color: 'text-amber-500', bg: 'bg-amber-50', title: 'Weekly Tips Drop', desc: "Every Monday, a new practical tip or system that members are actually using. Not general advice -- specific, tested, with savings estimates." },
    { icon: Target, color: 'text-[#C17855]', bg: 'bg-[#C17855] bg-opacity-10', title: 'Monthly Challenges', desc: "A focused challenge every month: no-spend week, pantry cook-down, subscription audit, repair project. Community-wide accountability." },
    { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50', title: 'Q&A Thread', desc: "Got a repair you're not sure about? A negotiation question? Post it. The community answers. So do we." },
    { icon: Trophy, color: 'text-emerald-500', bg: 'bg-emerald-50', title: 'Member Wins', desc: "A dedicated space to share victories. Got your insurance down? Fixed something yourself? Share it. Progress is contagious." },
    { icon: Lock, color: 'text-[#6B7A5C]', bg: 'bg-[#6B7A5C] bg-opacity-10', title: 'Exclusive Content Previews', desc: "New guides, tools, and resources go to community members first -- often at a discount, and sometimes free." },
    { icon: Sprout, color: 'text-[#6B7A5C]', bg: 'bg-[#EBE6D8]', title: 'Seasonal Growing Calendars', desc: "Region-specific planting guides updated each season. Know exactly what to plant when for your climate zone." },
  ];

  const handlePurchase = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const emailToUse = email || null;
      const { checkout_url } = await createCheckoutSession('community', emailToUse);
      window.location.href = checkout_url;
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create checkout. Please try again.', variant: 'destructive' });
      setIsProcessing(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Invalid Email', description: 'Enter a valid email.', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/community/verify/${encodeURIComponent(email)}`);
      const data = await res.json();
      if (data.is_member) {
        window.location.href = '/community/portal';
      } else {
        toast({ title: 'Not a Member', description: 'No active subscription found. Join below!', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to verify.', variant: 'destructive' });
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'tip': return <Lightbulb className="w-4 h-4 text-amber-500" />;
      case 'challenge': return <Target className="w-4 h-4 text-[#C17855]" />;
      case 'win': return <Trophy className="w-4 h-4 text-emerald-500" />;
      default: return <MessageSquare className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#F5F1E8]">
        <SimpleHeader />

        <main className="pt-24 pb-20 px-6">
          <div className="max-w-4xl mx-auto">
            {/* Banner */}
            <div className="rounded-xl overflow-hidden shadow-xl mb-10">
              <img src="/images/spendless-banner.png" alt="Spend Less. Live More." className="w-full h-auto" data-testid="product-banner-img" />
            </div>

            {/* Header */}
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="text-[#6B7A5C] text-sm uppercase tracking-wider font-semibold">Community</span>
                <span className="text-[#A89884]">&middot;</span>
                <span className="text-[#C17855] text-sm font-semibold">$9/month</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#2C1810] mb-4" data-testid="community-title">
                SpendLess Community
              </h1>
              <p className="text-[#5A5046] text-xl max-w-2xl mx-auto">
                You've started spending less. Now do it with other people doing the same thing.
              </p>

              {/* Live activity indicator */}
              {communityStats && (
                <div className="flex items-center justify-center gap-4 mt-6">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-sm text-[#5A5046]">Active Now</span>
                  </div>
                  {communityStats.active_challenge && (
                    <span className="text-sm text-[#C17855] font-medium">This Month's Challenge Live</span>
                  )}
                </div>
              )}
            </div>

            {/* Current Challenge Preview */}
            <Card className="bg-gradient-to-br from-[#C17855] to-[#B06745] text-white p-8 rounded-xl shadow-xl mb-12" data-testid="challenge-preview">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5" />
                <span className="text-sm font-bold uppercase tracking-wider opacity-80">This Month's Challenge</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">The April Challenge: The Subscription Audit</h3>
              <p className="opacity-90 leading-relaxed">
                Pull up every subscription you're paying for. Cancel at least one. Post your results --
                which subscription, how much you're saving annually. We'll compile the totals at the end of the month.
              </p>
            </Card>

            {/* Features Grid */}
            <h2 className="text-3xl font-bold text-[#2C1810] mb-8 text-center">What You Get as a Member</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              {features.map((f, i) => (
                <Card key={i} className="bg-white border border-[#E5DFD0] p-6 hover:shadow-md transition-shadow" data-testid={`feature-card-${i}`}>
                  <div className={`w-12 h-12 ${f.bg} rounded-lg flex items-center justify-center mb-4`}>
                    <f.icon className={`w-6 h-6 ${f.color}`} />
                  </div>
                  <h3 className="text-lg font-bold text-[#2C1810] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#5A5046] leading-relaxed">{f.desc}</p>
                </Card>
              ))}
            </div>

            {/* Live Community Preview */}
            {previewPosts.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-[#2C1810] mb-2 text-center">What's Happening Right Now</h2>
                <p className="text-[#5A5046] text-center mb-8">A glimpse of real community activity</p>
                <div className="space-y-4">
                  {previewPosts.map((post, i) => (
                    <Card key={i} className="bg-white border border-[#E5DFD0] p-5">
                      <div className="flex items-center gap-2 mb-2">
                        {getTypeIcon(post.type)}
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#A89884]">
                          {post.type === 'tip' ? 'Weekly Tip' : post.type === 'challenge' ? 'Challenge' : post.type === 'win' ? 'Member Win' : 'Q&A'}
                        </span>
                        <span className="text-xs text-[#A89884]">&middot; {post.author}</span>
                      </div>
                      <h3 className="font-bold text-[#2C1810] mb-1">{post.title}</h3>
                      <p className="text-sm text-[#5A5046] line-clamp-2">{post.content}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-[#A89884]">
                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {post.likes}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {post.replies?.length || 0} replies</span>
                      </div>
                    </Card>
                  ))}
                </div>
                <div className="text-center mt-4">
                  <p className="text-[#A89884] text-sm">Join to see all posts, reply, and participate in challenges</p>
                </div>
              </div>
            )}

            {/* Ground Rules */}
            <Card className="bg-[#EBE6D8] border-none p-8 mb-16">
              <h2 className="text-2xl font-bold text-[#2C1810] mb-4">Our Ground Rules</h2>
              <p className="text-[#5A5046] leading-relaxed">
                This community has one culture: <strong>practical action, honest results, no judgment.</strong> We don't
                care what you earn, where you live, or how much debt you have. We care whether you're making
                progress. Moralizing about others' spending choices, promoting products or services, or "guru
                energy" gets you removed.
              </p>
            </Card>

            {/* CTA Card */}
            <Card className="bg-white border-2 border-[#E5DFD0] shadow-xl p-10 max-w-xl mx-auto text-center" data-testid="community-cta-card">
              <img src="/images/spendless-logo.png" alt="SpendLess" className="w-16 h-16 rounded-full shadow-md mx-auto mb-4" />
              <div className="text-5xl font-bold text-[#C17855] mb-1">$9</div>
              <p className="text-[#5A5046] text-sm mb-6">per month &middot; cancel anytime</p>

              <ul className="space-y-3 text-left mb-8">
                {['Weekly Tips Drop', 'Monthly Challenges', 'Q&A Thread', 'Member Wins', 'Exclusive Content Previews', 'Seasonal Growing Calendars'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#6B7A5C] flex-shrink-0" />
                    <span className="text-[#2C1810]">{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={handlePurchase}
                disabled={isProcessing}
                className="w-full bg-[#C17855] hover:bg-[#B06745] text-white px-8 py-6 text-lg rounded-lg transition-all shadow-md hover:shadow-lg mb-4"
                data-testid="community-purchase-btn"
              >
                {isProcessing ? 'Redirecting...' : 'Join the Community — $9/mo'}
              </Button>
              <p className="text-[#A89884] text-xs mb-8">First month free with any SpendLess guide purchase</p>

              <div className="border-t border-[#E5DFD0] pt-6">
                <p className="text-[#2C1810] font-semibold mb-3 text-sm">Already a member?</p>
                <form onSubmit={handleUnlock} className="flex gap-2">
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="flex-1 border-[#E5DFD0]" data-testid="community-email-input" />
                  <Button type="submit" variant="outline" className="border-2 border-[#6B7A5C] text-[#6B7A5C] hover:bg-[#6B7A5C] hover:text-white px-4" data-testid="community-unlock-btn">
                    Enter
                  </Button>
                </form>
              </div>
            </Card>
          </div>
        </main>

        <Footer />
      </div>
      <Toaster />
    </>
  );
};

export default CommunityPage;
